// Court colours, ported from the iOS app's lib/helpers/pigmentHelper.dart.
//
// WHY THIS EXISTS: `rule.color` used to always be a hex string, and the widget
// could drop it straight into `background`. It can now also be a SENTINEL like
// "gradient:northernLights" — the app stores gradients in the same field so
// nothing changed on the wire. Feeding that to CSS yields no background at all,
// so any match using a Horizons or Courtside colour would render unstyled here.
//
// Keep in sync with pigmentHelper.dart. `color` is the ANCHOR: the single
// representative colour used wherever a gradient can't go (the watch, the Live
// Activity, and here for tinting icons).

export const PACK_CLASSIC = "summer21"; // displayed as "Classic"
export const PACK_PASTELS = "spring22";
export const PACK_HORIZONS = "horizons";
export const PACK_COURTSIDE = "courtside";

const WHITE = "#FFFFFF";
const BLACK = "#000000";

// name, color (anchor), textColor, brightness, pack, and for gradients:
// key (the sentinel stored in rule.color), stops, stopsAt, angle, scrims.
export const pigments = [
  // Classic
  { name: "ball", color: "#FEF100", textColor: BLACK, brightness: "light", pack: PACK_CLASSIC },
  { name: "snow", color: WHITE, textColor: BLACK, brightness: "light", pack: PACK_CLASSIC },
  { name: "hard", color: "#03A9F4", textColor: WHITE, brightness: "dark", pack: PACK_CLASSIC },
  { name: "grass", color: "#7EC850", textColor: WHITE, brightness: "dark", pack: PACK_CLASSIC },
  { name: "clay", color: "#E3783B", textColor: WHITE, brightness: "dark", pack: PACK_CLASSIC },

  // Pastels
  { name: "alpineGreen", color: "#576856", textColor: WHITE, brightness: "light", pack: PACK_PASTELS },
  { name: "nectarine", color: "#F46255", textColor: WHITE, brightness: "light", pack: PACK_PASTELS },
  { name: "lemonZest", color: "#F0D56E", textColor: BLACK, brightness: "light", pack: PACK_PASTELS },
  { name: "abyssBlue", color: "#465364", textColor: WHITE, brightness: "light", pack: PACK_PASTELS },
  { name: "chalkPink", color: "#EFD7D5", textColor: BLACK, brightness: "light", pack: PACK_PASTELS },

  // Horizons — skies & seas
  {
    name: "pride",
    key: "gradient:pride",
    color: "#277DA1",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_HORIZONS,
    stops: ["#E63946", "#F3722C", "#F9C74F", "#43AA8B", "#277DA1", "#7B2CBF"],
    angle: 135,
    scrimTop: 0.28,
    scrimBottom: 0.28,
  },
  {
    name: "patriot",
    key: "gradient:patriot",
    color: "#3E5AA8",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_HORIZONS,
    stops: ["#0A2463", "#3E5AA8", "#F2F4F8", "#E5484D", "#A4161A"],
    stopsAt: [0, 0.26, 0.52, 0.76, 1],
    angle: 160,
    scrimTop: 0.25,
    scrimBottom: 0.25,
  },
  {
    name: "californiaSunset",
    key: "gradient:californiaSunset",
    color: "#D94F4F",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_HORIZONS,
    stops: ["#2B1B4A", "#7B2D6B", "#D94F4F", "#F79256", "#FBD87F"],
    angle: 165,
    scrimTop: 0.15,
    scrimBottom: 0.32,
  },
  {
    name: "caribbeanSea",
    key: "gradient:caribbeanSea",
    color: "#22C4D4",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_HORIZONS,
    stops: ["#0B5FA5", "#128FC4", "#22C4D4", "#7FE9DA", "#F2E2BE"],
    angle: 150,
  },
  {
    name: "northernLights",
    key: "gradient:northernLights",
    color: "#1FA36A",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_HORIZONS,
    // The only pigment with uneven stop positions — they carry the shape of
    // the aurora band and must not be redistributed evenly.
    stops: ["#03060F", "#0B2036", "#1FA36A", "#57E39B", "#6B45C9", "#2A1240"],
    stopsAt: [0, 0.22, 0.44, 0.57, 0.82, 1],
    angle: 200,
  },

  // Courtside — pure tennis
  {
    name: "clayCourt",
    key: "gradient:clayCourt",
    color: "#A34327",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_COURTSIDE,
    stops: ["#5C1F12", "#A34327", "#D97B4F", "#F0C9A0"],
    angle: 150,
    scrimBottom: 0.22,
  },
  {
    name: "centreCourt",
    key: "gradient:centreCourt",
    color: "#1E6F4C",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_COURTSIDE,
    stops: ["#0B3D2E", "#1E6F4C", "#57A863", "#E4E0D2"],
    angle: 155,
    scrimBottom: 0.25,
  },
  {
    name: "nightMatch",
    key: "gradient:nightMatch",
    color: "#1D4E89",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_COURTSIDE,
    stops: ["#060C1B", "#0F2445", "#1D4E89", "#3A8DDE"],
    angle: 170,
  },
  {
    name: "opticYellow",
    key: "gradient:opticYellow",
    color: "#CCDC29",
    textColor: BLACK,
    brightness: "light",
    pack: PACK_COURTSIDE,
    stops: ["#4A5000", "#8FA300", "#CCDC29", "#EAFF6B"],
    angle: 140,
  },
  {
    name: "retroAce",
    key: "gradient:retroAce",
    color: "#7B2FF7",
    textColor: WHITE,
    brightness: "dark",
    pack: PACK_COURTSIDE,
    stops: ["#2B1055", "#7B2FF7", "#F107A3", "#37D5D6"],
    angle: 145,
    scrimTop: 0.22,
    scrimBottom: 0.22,
  },
];

const FALLBACK = pigments[0]; // Ball

// Resolve whatever is in rule.color — a hex, a "gradient:x" sentinel, or
// something from a newer app build we don't know yet.
export function getPigment(colorKey) {
  if (!colorKey) return FALLBACK;
  const key = String(colorKey).trim();

  const byKey = pigments.find((p) => p.key === key);
  if (byKey) return byKey;

  const upper = key.toUpperCase();
  const byHex = pigments.find((p) => p.color.toUpperCase() === upper);
  if (byHex) return byHex;

  // Unknown hex from a future palette: treat it as a solid so the page still
  // paints something sensible rather than falling back to an unrelated colour.
  if (/^#[0-9A-F]{6}$/i.test(key)) {
    return { name: "custom", color: key, textColor: textColorFor(key), brightness: luminance(key) > 0.55 ? "light" : "dark" };
  }

  return FALLBACK;
}

export function isGradient(pigment) {
  return Array.isArray(pigment.stops) && pigment.stops.length > 0;
}

// Relative luminance (WCAG), used to decide black vs white content.
export function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return 0;
  const int = parseInt(m[1], 16);
  const srgb = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function textColorFor(hex) {
  return luminance(hex) > 0.55 ? BLACK : WHITE;
}

// The CSS background for a match, scrim included. The app's angles are already
// in the CSS convention (0deg = to top, 90deg = to right), so they pass
// through unchanged — the Flutter side is the one that has to convert.
//
// The scrim rides along as a SECOND background layer rather than an overlay
// element: in CSS the first layer paints on top, which gives exactly the app's
// "black wash over the gradient" without adding a positioned div that would
// otherwise cover the scoreboard.
export function matchBackground(colorKey) {
  const pigment = getPigment(colorKey);
  if (!isGradient(pigment)) return pigment.color;

  const stops = pigment.stops.map((hex, i) => {
    const at =
      pigment.stopsAt && pigment.stopsAt[i] != null
        ? ` ${(pigment.stopsAt[i] * 100).toFixed(2)}%`
        : "";
    return `${hex}${at}`;
  });
  const ramp = `linear-gradient(${pigment.angle || 135}deg, ${stops.join(", ")})`;

  const scrim = matchScrim(colorKey);
  return scrim ? `${scrim}, ${ramp}` : ramp;
}

// Text/icon colour over the match background. Every pigment declares its own,
// gradients included — Optic Yellow asks for black, like Ball.
export function matchTextColor(colorKey) {
  return getPigment(colorKey).textColor;
}

// The anchor — one representative colour, for tinting things that can't take
// a gradient (the racket icon filter, badge choice).
export function matchAnchorColor(colorKey) {
  return getPigment(colorKey).color;
}

// True for schemes whose page reads as light (Ball, Snow, Lemon Zest, and the
// Optic Yellow gradient). Gradients are judged by their anchor like solids,
// rather than assumed dark — that assumption is what made Optic Yellow
// inconsistent with itself.
export function isLightScheme(colorKey) {
  return luminance(getPigment(colorKey).color) > 0.55;
}

// Legibility scrim the app lays over the louder gradients, as a CSS overlay
// (null when the pigment doesn't ask for one).
export function matchScrim(colorKey) {
  const pigment = getPigment(colorKey);
  if (!isGradient(pigment)) return null;
  const top = pigment.scrimTop || 0;
  const bottom = pigment.scrimBottom || 0;
  if (top === 0 && bottom === 0) return null;
  return `linear-gradient(180deg, rgba(0,0,0,${top}), rgba(0,0,0,${bottom}))`;
}

// White icons on dark schemes, black on light ones — the widget tints PNG
// icons with a CSS filter, so this returns the invert() it needs.
export function iconFilter(colorKey) {
  return isLightScheme(colorKey) ? "invert(0%)" : "invert(100%)";
}

// Which store-badge artwork to use: the dark-text badge on light schemes, the
// light-text one otherwise. Keyed off the scheme rather than a hex lookup, so
// gradients (whose anchors were never in the old table) resolve too.
export function usesDarkStoreBadge(colorKey) {
  return isLightScheme(colorKey);
}

// ---------------------------------------------------------------------------
// Score-card tiles
//
// The card at the top of the page is the app's MatchScoreGizmo, so these
// mirror the ExtRule methods matchScoreBoard.dart reads. They are separate
// from matchTextColor because a tile has its OWN fill — the colour that reads
// on the page is not the colour that reads on the tile.
// ---------------------------------------------------------------------------

// A set tile is filled with the scheme's own colour (ExtRule.getSetTileColor).
// Snow is the exception: its colour IS the page, so the app paints those white.
export function matchSetTileColor(colorKey) {
  const pigment = getPigment(colorKey);
  return pigment.name === "snow" ? WHITE : pigment.color;
}

// Text on a set tile: black on the schemes that declare black content, white
// on the rest. The app arrives at the same thing via
// filledColor(rule.getLightInvertedColor(), false, false).
//
// It spells the black-text schemes out as a literal list (Ball, Chalk Pink)
// written before the Pastels and Courtside packs existed, so Lemon Zest and
// Optic Yellow still fall through to white there — unreadable on their own
// pale fill. Testing the pigment's declared textColor reproduces the app
// exactly for every colour that list covers, and gets those two right.
export function matchSetTileTextColor(colorKey) {
  return getPigment(colorKey).textColor === BLACK ? BLACK : WHITE;
}

// The tiles for the game in play are white, with the scheme colour as their
// text (ExtRule.getGameTileTextColor) — black where that colour is too pale to
// read on white, by the same test as above.
export function matchGameTileTextColor(colorKey) {
  const pigment = getPigment(colorKey);
  return pigment.textColor === BLACK ? BLACK : pigment.color;
}

// A tile's stroke is its own fill, so it reads as one solid block; on light
// schemes, where the fill and the page are the same hue, it takes a dark
// hairline instead or the tile has no edge at all (_tileBorder).
export function matchTileBorderColor(colorKey, tileColor) {
  return isLightScheme(colorKey) ? "rgba(0,0,0,0.54)" : tileColor;
}

// The serving indicator: tennis-ball chartreuse, black on the light schemes
// where that yellow disappears (matchServeBallColor).
export function matchServeBallColor(colorKey) {
  return isLightScheme(colorKey) ? BLACK : "#C6F135";
}

// The card's hairline (matchCardBorderColor). Returned flat: the caller fades
// it along the card's edge the way puma_ui's Glass paints its stroke.
export function matchCardBorderColor(colorKey) {
  return isLightScheme(colorKey) ? BLACK : WHITE;
}
